"use client";

// Keeps the local data in step with one JSON file in your Google Drive so the same
// invoices are available in every Chrome profile, browser and computer.
//
// - Changes are pushed a few seconds after you make them (while signed in).
// - On connect / "Sync now", newer data in Drive is pulled in automatically
//   if this device has no unsynced changes; otherwise you choose which copy wins.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useInvoiceStore } from "@/lib/invoice/store";
import { idbGet, idbSet } from "@/lib/invoice/idb";
import { deleteDriveFile, deviceLabel, download, downloadDocFile, findFile, getMeta, requestToken, upload, uploadDocFile, whoAmI } from "@/lib/invoice/gdrive";
import { downloadBlob, todayISO, uid } from "@/lib/invoice/utils";
import { Button, Modal, useFeedback } from "./ui";
import GoogleButton from "./GoogleButton";
import { googleClientId, loadServerConfig, signInWithGoogle } from "@/lib/invoice/account";

const DriveContext = createContext(null);
// The site's own Google OAuth client (built in, or published by the account server),
// so users just click "Connect Google Drive".
const builtInClientId = () => googleClientId();
const TOKEN_KEY = "invoice-studio-drive-token";

const snapshot = (s) => ({ settings: s.settings, brands: s.brands, clients: s.clients, projects: s.projects, invoices: s.invoices, sales: s.sales, documents: s.documents, letters: s.letters, driveDeletes: s.driveDeletes, trash: s.trash });

export function DriveSyncProvider({ children }) {
  const store = useInvoiceStore();
  const { toast } = useFeedback();
  const [cfg, setCfg] = useState(null);
  const [siteClientId, setSiteClientId] = useState(builtInClientId());
  useEffect(() => {
    // the site's Client ID is set in the admin panel – pick up changes
    loadServerConfig().then((c) => {
      if (!c.googleClientId) return;
      setSiteClientId(c.googleClientId);
      setCfg((x) => (x && (!x.clientId || x.clientId === siteClientId) ? { ...x, clientId: c.googleClientId } : x));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps // persisted per device: { enabled, clientId, fileId, lastRev, dirty, email, lastSyncAt, deviceId }
  const [token, setToken] = useState(null);
  const [status, setStatus] = useState("off"); // off | needs-auth | syncing | synced | conflict | error
  const [error, setError] = useState("");
  const [renewFailed, setRenewFailed] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  const [conflict, setConflict] = useState(null);
  const [needLogin, setNeedLogin] = useState(null); // encrypted Drive data from another device
  const [cred, setCred] = useState({ username: "", password: "" });
  const skipDirty = useRef(false);
  const busy = useRef(false);
  const storeRef = useRef(store);
  storeRef.current = store;

  const saveCfg = useCallback((patch) => {
    setCfg((c) => {
      const next = { ...c, ...patch };
      idbSet("driveState", next).catch(() => {});
      return next;
    });
  }, []);

  // load per-device state
  useEffect(() => {
    idbGet("driveState")
      .catch(() => null)
      .then((s) => {
        const c = { enabled: false, clientId: builtInClientId(), fileId: "", lastRev: "", dirty: false, email: "", lastSyncAt: "", deviceId: uid(), ...(s || {}) };
        if (!c.clientId) c.clientId = builtInClientId();
        setCfg(c);
        let t = null;
        try {
          t = JSON.parse(localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || "null");
        } catch {
          /* ignore */
        }
        const valid = t && t.expiresAt > Date.now();
        if (valid) setToken(t);
        setStatus(!c.enabled ? "off" : valid ? "synced" : "needs-auth");
      });
  }, []);

  // mark local changes as unsynced (compare data identity, ignore our own pulls)
  const lastSeen = useRef(null);
  useEffect(() => {
    if (!store.ready || !cfg) {
      lastSeen.current = null;
      return;
    }
    const cur = [store.clients, store.projects, store.invoices, store.sales, store.brands, store.settings, store.documents, store.letters, store.driveDeletes, store.trash];
    const prev = lastSeen.current;
    lastSeen.current = cur;
    if (!prev || cur.every((x, i) => x === prev[i])) return;
    if (skipDirty.current) {
      skipDirty.current = false;
      return;
    }
    if (cfg.enabled && !cfg.dirty) saveCfg({ dirty: true });
  }, [store.ready, cfg, store.clients, store.projects, store.invoices, store.sales, store.brands, store.settings, store.documents, store.letters, store.driveDeletes, store.trash, saveCfg]);

  const tokenValid = token && token.expiresAt > Date.now();

  const pull = useCallback(
    async (tk, meta) => {
      const json = await download(tk.token, meta.id);
      let obj;
      try {
        obj = await storeRef.current.openEnvelope(json);
      } catch (e) {
        if (e.code === "NEEDS_LOGIN") {
          e.envelope = json;
          e.meta = meta;
        }
        throw e;
      }
      skipDirty.current = true;
      storeRef.current.importAll(obj);
      saveCfg({ fileId: meta.id, lastRev: meta.appProperties?.rev || "", dirty: false, lastSyncAt: new Date().toISOString() });
    },
    [saveCfg]
  );

  const push = useCallback(
    async (tk, fileId) => {
      const rev = uid();
      const payload = await storeRef.current.protect({ ...snapshot(storeRef.current), app: "invoice-studio", syncedAt: new Date().toISOString() });
      const meta = await upload(tk.token, fileId || null, payload, {
        rev,
        savedAt: new Date().toISOString(),
        device: deviceLabel(),
        deviceId: cfg?.deviceId || "",
      });
      saveCfg({ fileId: meta.id, lastRev: rev, dirty: false, lastSyncAt: new Date().toISOString() });
    },
    [saveCfg, cfg?.deviceId]
  );

  // Tax document files: upload ones Drive doesn't have yet, remove ones deleted for good.
  const docsBusy = useRef(false);
  const syncDocs = useCallback(async (tk) => {
    if (docsBusy.current) return;
    docsBusy.current = true;
    try {
      const s = storeRef.current;
      const uploaded = [];
      for (const d of s.documents || []) {
        if (d.driveId) continue;
        const raw = await s.getRawDocFile(d.id);
        if (!raw) continue; // added on another device, not here
        const driveId = await uploadDocFile(tk.token, { docId: d.id, name: d.fileName || d.title, blob: raw, encrypted: !!d.encrypted });
        uploaded.push({ id: d.id, driveId });
      }
      if (uploaded.length) s.saveDocuments(uploaded);
      const del = [...(s.driveDeletes || [])];
      for (const id of del) await deleteDriveFile(tk.token, id);
      if (del.length) s.clearDriveDeletes(del);
    } catch (e) {
      if (e.code === 401) setStatus("needs-auth");
      else setError(`Documents: ${e.message}`);
    } finally {
      docsBusy.current = false;
    }
  }, []);

  const sync = useCallback(
    async (tk = token, { initial = false } = {}) => {
      if (!cfg?.enabled && !initial) return;
      if (!tk || tk.expiresAt <= Date.now()) {
        setStatus("needs-auth");
        return;
      }
      if (busy.current || !storeRef.current.ready) return;
      busy.current = true;
      setStatus("syncing");
      setError("");
      try {
        let meta = cfg.fileId ? await getMeta(tk.token, cfg.fileId).catch(() => null) : null;
        if (!meta) meta = await findFile(tk.token);
        const s = storeRef.current;
        const localEmpty = !s.clients.length && !s.invoices.length && !s.projects.length && !s.sales.length;
        if (!meta) {
          await push(tk, null);
          if (initial) toast("Google Drive sync is on – your data was saved to Drive", { tone: "success" });
        } else if ((meta.appProperties?.rev || "") === cfg.lastRev) {
          if (cfg.dirty) await push(tk, meta.id);
        } else if (!cfg.dirty || localEmpty) {
          await pull(tk, meta);
          toast(`Loaded the latest data from Google Drive${meta.appProperties?.device ? ` (saved on ${meta.appProperties.device})` : ""}`, { tone: "success" });
        } else {
          setConflict({ meta });
          setStatus("conflict");
          busy.current = false;
          return;
        }
        setStatus("synced");
        setTimeout(() => syncDocs(tk), 0);
      } catch (e) {
        if (e.code === 401) {
          setToken(null);
          setStatus("needs-auth");
        } else if (e.code === "NEEDS_LOGIN") {
          setNeedLogin({ envelope: e.envelope, meta: e.meta, error: "" });
          setStatus("conflict");
        } else {
          setError(e.message);
          setStatus("error");
        }
      } finally {
        busy.current = false;
      }
    },
    [cfg, token, push, pull, toast, syncDocs]
  );

  // auto-push shortly after local changes
  useEffect(() => {
    if (!cfg?.enabled || !cfg.dirty || !tokenValid || status === "conflict") return;
    const t = setTimeout(() => sync(), 3000);
    return () => clearTimeout(t);
  }, [cfg?.enabled, cfg?.dirty, tokenValid, status, sync, store.invoices, store.clients, store.projects, store.sales]);

  // on open (with a still-valid sign-in) check Drive for changes made elsewhere
  const openedCheck = useRef(false);
  useEffect(() => {
    if (openedCheck.current || !store.ready || !cfg?.enabled || !tokenValid) return;
    openedCheck.current = true;
    sync();
  }, [store.ready, cfg?.enabled, tokenValid, sync]);

  // another tab renewed the Google sign-in → use it here too
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== TOKEN_KEY || !e.newValue) return;
      try {
        const t = JSON.parse(e.newValue);
        if (t?.expiresAt > Date.now()) {
          setToken(t);
          setRenewFailed(false);
          if (status === "needs-auth") setTimeout(() => syncRef.current(t), 0);
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [status]);

  // come back to the tab / reconnect to the internet → sync straight away
  useEffect(() => {
    if (!cfg?.enabled) return;
    const onFocus = () => {
      if (document.visibilityState === "visible" && tokenValid && navigator.onLine !== false) syncRef.current();
    };
    const onOnline = () => {
      setOffline(false);
      if (tokenValid) syncRef.current();
    };
    const onOffline = () => setOffline(true);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [cfg?.enabled, tokenValid]);

  // Google sign-ins last about an hour and can only be renewed after a click, so renew
  // quietly on the next click anywhere (no need to go looking for a button).
  const renewing = useRef(0);
  useEffect(() => {
    if (!cfg?.enabled || !cfg.clientId) return;
    const soon = token && token.expiresAt - Date.now() < 5 * 60000;
    if (status !== "needs-auth" && !soon) return;
    const onClick = () => {
      if (Date.now() - renewing.current < 60000 || navigator.onLine === false) return;
      renewing.current = Date.now();
      signInRef
        .current(cfg.clientId, { quiet: true })
        .then((tk) => setTimeout(() => syncRef.current(tk), 0))
        .then(() => setRenewFailed(false))
        .catch(() => setRenewFailed(true)); // banner turns red and explains
    };
    document.addEventListener("pointerdown", onClick, true);
    return () => document.removeEventListener("pointerdown", onClick, true);
  }, [cfg?.enabled, cfg?.clientId, status, token]);

  // gentle background check for other devices' changes every 2 minutes
  useEffect(() => {
    if (!cfg?.enabled || !tokenValid) return;
    const t = setInterval(() => sync(), 120000);
    return () => clearInterval(t);
  }, [cfg?.enabled, tokenValid, sync]);

  const signIn = useCallback(
    async (clientId = cfg?.clientId, { initial = false } = {}) => {
      if (!clientId) throw new Error("Add your Google OAuth Client ID first (Settings → Data & backup).");
      const tk = await requestToken(clientId, { hint: cfg?.email || undefined });
      setToken(tk);
      try {
        localStorage.setItem(TOKEN_KEY, JSON.stringify(tk)); // shared by all tabs until it expires (≈1 hour)
      } catch {
        /* ignore */
      }
      const email = await whoAmI(tk.token);
      saveCfg({ enabled: true, clientId, email: email || cfg?.email || "" });
      return tk;
    },
    [cfg, saveCfg]
  );

  const api = useMemo(
    () => ({
      cfg,
      status,
      offline,
      renewFailed,
      error,
      tokenValid,
      supported: true,
      envClientId: siteClientId,
      setClientId: (clientId) => saveCfg({ clientId }),
      connect: async (clientId) => {
        try {
          const tk = await signIn(clientId, { initial: true });
          // cfg in the sync closure is stale right after enabling – pass through a fresh read
          setTimeout(() => syncRef.current(tk, { initial: true }), 0);
        } catch (e) {
          toast(e.message, { tone: "error" });
        }
      },
      reconnect: async () => {
        try {
          const tk = await signIn();
          setTimeout(() => syncRef.current(tk), 0);
        } catch (e) {
          toast(e.message, { tone: "error" });
        }
      },
      syncNow: () => sync(),
      // A document's file: from this device, or fetched from Drive the first time it's opened here.
      fetchDocFile: async (doc) => {
        const s = storeRef.current;
        const local = await s.getDocFile(doc);
        if (local) return local;
        if (!doc.driveId) return null;
        if (!tokenValid) throw new Error("This document is in your Google Drive – reconnect Drive sync to open it on this device.");
        const raw = await downloadDocFile(token.token, doc.driveId);
        await s.putRawDocFile(doc.id, raw);
        return s.getDocFile(doc);
      },
      disconnect: () => {
        setToken(null);
        try {
          localStorage.removeItem(TOKEN_KEY);
          sessionStorage.removeItem(TOKEN_KEY);
        } catch {
          /* ignore */
        }
        saveCfg({ enabled: false, fileId: "", lastRev: "", dirty: false });
        setStatus("off");
        try {
          window.google?.accounts?.oauth2?.revoke?.(token?.token);
        } catch {
          /* ignore */
        }
      },
    }),
    [cfg, status, offline, renewFailed, error, tokenValid, saveCfg, signIn, sync, toast, token, siteClientId]
  );

  const syncRef = useRef(sync);
  syncRef.current = sync;
  const signInRef = useRef(signIn);
  signInRef.current = signIn;

  const resolveConflict = async (useDrive) => {
    const meta = conflict.meta;
    setConflict(null);
    setStatus("syncing");
    try {
      if (useDrive) {
        // keep a safety copy of this device's version first
        downloadBlob(
          new Blob([JSON.stringify({ ...snapshot(storeRef.current), exportedAt: new Date().toISOString() }, null, 1)], { type: "application/json" }),
          `invoice-studio-this-device-before-drive-${todayISO()}.json`
        );
        await pull(token, meta);
        toast("Loaded the Google Drive version. This device’s previous data was downloaded as a backup file.", { tone: "success" });
      } else {
        await push(token, meta.id);
        toast("This device’s version was saved to Google Drive", { tone: "success" });
      }
      setStatus("synced");
    } catch (e) {
      if (e.code === "NEEDS_LOGIN") {
        setNeedLogin({ envelope: e.envelope, meta: e.meta, error: "" });
        return;
      }
      setError(e.message);
      setStatus("error");
    }
  };

  const submitLogin = async () => {
    try {
      const obj = await storeRef.current.openEnvelopeWithLogin(needLogin.envelope, cred.username, cred.password);
      skipDirty.current = true;
      storeRef.current.importAll(obj);
      const meta = needLogin.meta;
      saveCfg({ fileId: meta.id, lastRev: meta.appProperties?.rev || "", dirty: false, lastSyncAt: new Date().toISOString() });
      setNeedLogin(null);
      setCred({ username: "", password: "" });
      setStatus("synced");
      toast("Signed in – loaded your data from Google Drive", { tone: "success" });
    } catch (e) {
      setNeedLogin((n) => ({ ...n, error: e.message }));
    }
  };

  return (
    <DriveContext.Provider value={api}>
      {children}
      <Modal
        open={!!needLogin}
        onClose={() => {}}
        title="Your Google Drive data is protected"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setNeedLogin(null); setStatus("error"); setError("Sign in to open the protected data in Google Drive."); }}>Not now</Button>
            <Button onClick={submitLogin}>Sign in & load</Button>
          </>
        }
      >
        {needLogin && (
          <form
            className="space-y-3 text-sm"
            onSubmit={(e) => {
              e.preventDefault();
              submitLogin();
            }}
          >
            <div className="text-slate-600">
              The data in this Google Drive is locked with a login. Sign in with the Google account you use for this workspace, or enter your user ID and password, to open it on this device.
            </div>
            {needLogin.envelope?.auth?.users?.some((u) => u.kind === "google") && (
              <div className="rounded-lg bg-slate-50 p-3">
                <GoogleButton
                  onCredential={async (credential) => {
                    try {
                      const r = await signInWithGoogle(credential, storeRef.current);
                      const obj = await storeRef.current.openEnvelopeWithGoogle(needLogin.envelope, { sub: r.sub, vaultKey: r.vaultKey });
                      skipDirty.current = true;
                      storeRef.current.importAll(obj);
                      const meta = needLogin.meta;
                      saveCfg({ fileId: meta.id, lastRev: meta.appProperties?.rev || "", dirty: false, lastSyncAt: new Date().toISOString() });
                      setNeedLogin(null);
                      setStatus("synced");
                      setError("");
                    } catch (e) {
                      setNeedLogin((n) => ({ ...n, error: e.message }));
                    }
                  }}
                />
              </div>
            )}
            <input className="w-full rounded-lg border border-slate-200 px-3 py-2" placeholder="User ID" autoComplete="username" value={cred.username} onChange={(e) => setCred({ ...cred, username: e.target.value })} />
            <input className="w-full rounded-lg border border-slate-200 px-3 py-2" placeholder="Password" type="password" autoComplete="current-password" value={cred.password} onChange={(e) => setCred({ ...cred, password: e.target.value })} />
            {needLogin.error && <div className="text-red-600">{needLogin.error}</div>}
            <button type="submit" className="hidden" />
          </form>
        )}
      </Modal>
      <Modal
        open={!!conflict}
        onClose={() => {}}
        title="Changes on two devices"
        footer={
          <>
            <Button variant="secondary" onClick={() => resolveConflict(false)}>Keep this device’s version</Button>
            <Button onClick={() => resolveConflict(true)}>Use Google Drive version</Button>
          </>
        }
      >
        {conflict && (
          <div className="space-y-2 text-sm text-slate-700">
            <div>
              Google Drive has newer data saved
              {conflict.meta.appProperties?.device ? <> from <b>{conflict.meta.appProperties.device}</b></> : ""}
              {conflict.meta.modifiedTime ? <> at {new Date(conflict.meta.modifiedTime).toLocaleString()}</> : ""}, and this device also has changes that
              weren’t synced yet.
            </div>
            <div>Choose which copy to keep. If you use the Drive version, this device’s data is downloaded as a backup file first so nothing is lost.</div>
          </div>
        )}
      </Modal>
    </DriveContext.Provider>
  );
}

export function useDrive() {
  return useContext(DriveContext) || { cfg: null, status: "off" };
}
